import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Building2, Calendar, Download, Trash2, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'aventix2026'; // Changez ce mot de passe!

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Connexion réussie');
      fetchContacts();
    } else {
      toast.error('Mot de passe incorrect');
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact/`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (contact) => {
    if (!contact.attachment_data) return;
    
    const byteCharacters = atob(contact.attachment_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contact.attachment_mimetype });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = contact.attachment_filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/${id}`);
      toast.success('Contact supprimé');
      fetchContacts();
      setSelectedContact(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-CA') + ' à ' + date.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl">Administration Aventix</CardTitle>
            <CardDescription>Entrez le mot de passe pour accéder aux messages</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez le mot de passe"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                Se connecter
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate('/')}
              >
                Retour au site
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex items-center"
              >
                <ArrowLeft className="mr-2" size={20} />
                Retour au site
              </Button>
              <h1 className="text-2xl font-bold text-slate-900">Messages de contact</h1>
              <Badge variant="secondary">{contacts.length} messages</Badge>
            </div>
            <Button onClick={fetchContacts} disabled={loading}>
              {loading ? 'Chargement...' : 'Actualiser'}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && contacts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Chargement des messages...</p>
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Aucun message pour le moment</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Messages List */}
            <div className="lg:col-span-1 space-y-3">
              {contacts.map((contact) => (
                <Card
                  key={contact.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedContact?.id === contact.id ? 'ring-2 ring-blue-600' : ''
                  }`}
                  onClick={() => setSelectedContact(contact)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-slate-900">{contact.name}</h3>
                      {contact.attachment_filename && (
                        <Badge variant="outline" className="text-xs">
                          📎
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-1">{contact.company || 'Sans entreprise'}</p>
                    <p className="text-xs text-slate-500">{formatDate(contact.created_at)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Message Detail */}
            <div className="lg:col-span-2">
              {selectedContact ? (
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-2xl">{selectedContact.name}</CardTitle>
                        <CardDescription>{formatDate(selectedContact.created_at)}</CardDescription>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteContact(selectedContact.id)}
                      >
                        <Trash2 size={16} className="mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Contact Info */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start space-x-3">
                        <Mail className="text-blue-600 mt-1" size={20} />
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <a
                            href={`mailto:${selectedContact.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {selectedContact.email}
                          </a>
                        </div>
                      </div>
                      {selectedContact.phone && (
                        <div className="flex items-start space-x-3">
                          <Phone className="text-blue-600 mt-1" size={20} />
                          <div>
                            <p className="text-xs text-slate-500">Téléphone</p>
                            <a
                              href={`tel:${selectedContact.phone}`}
                              className="text-blue-600 hover:underline"
                            >
                              {selectedContact.phone}
                            </a>
                          </div>
                        </div>
                      )}
                      {selectedContact.company && (
                        <div className="flex items-start space-x-3">
                          <Building2 className="text-blue-600 mt-1" size={20} />
                          <div>
                            <p className="text-xs text-slate-500">Entreprise</p>
                            <p className="text-slate-900">{selectedContact.company}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Message */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Message</h4>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-slate-700 whitespace-pre-wrap">{selectedContact.message}</p>
                      </div>
                    </div>

                    {/* Attachment */}
                    {selectedContact.attachment_filename && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Fichier joint</h4>
                        <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900">
                              {selectedContact.attachment_filename}
                            </p>
                            <p className="text-sm text-slate-600">
                              {(selectedContact.attachment_size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <Button
                            onClick={() => downloadFile(selectedContact)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Download size={16} className="mr-2" />
                            Télécharger
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="flex items-center justify-center h-96 bg-white rounded-lg border-2 border-dashed border-slate-300">
                  <div className="text-center">
                    <Eye className="mx-auto text-slate-400 mb-4" size={48} />
                    <p className="text-slate-600">Sélectionnez un message pour voir les détails</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
